import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDocs, query, where } from "firebase/firestore";
import teams from "./teams";

const TIERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function Flag({ cc, size = 16 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img
      src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt=""
      width={size}
      height={h}
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

function PickEntry({ user, contest, existingEntry, onSaved }) {
  const [entryName, setEntryName] = useState("");
  const [picks, setPicks] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);

  useEffect(() => {
    if (existingEntry) { setEntryName(existingEntry.entryName); setPicks(existingEntry.picks || {}); }
    else { setEntryName(""); setPicks({}); }
    setSubmitted(false); setSavedEntry(null); setError("");
  }, [existingEntry]);

  const teamsForTier = (tier) => teams.filter((t) => t.tier === tier);
  const handlePick = (tier, teamId) => setPicks((prev) => ({ ...prev, ["tier" + tier]: teamId }));
  const allPicked = TIERS.every((t) => picks["tier" + t]);
  const picksComplete = TIERS.filter((t) => picks["tier" + t]).length;
  const getTeam = (teamId) => teams.find((t) => t.id === teamId);

  // Find which groups are already picked
  const pickedGroups = () => {
    const groups = {};
    TIERS.forEach((tier) => {
      const teamId = picks["tier" + tier];
      if (teamId) {
        const team = getTeam(teamId);
        if (team) groups[team.group] = (groups[team.group] || 0) + 1;
      }
    });
    return groups;
  };

  const save = async () => {
    setError("");
    if (!entryName.trim()) { setError("Please give your entry a name."); return; }
    if (!allPicked) { setError("Please pick one team from every tier (" + picksComplete + "/10 complete)."); return; }
    setSaving(true);
    try {
      const nameQ = query(collection(db, "entries"), where("contestId", "==", contest.id), where("userId", "==", user.uid), where("entryName", "==", entryName.trim()));
      const nameSnap = await getDocs(nameQ);
      const duplicate = nameSnap.docs.find((d) => !existingEntry || d.id !== existingEntry.id);
      if (duplicate) { setError("You already have an entry called \"" + entryName.trim() + "\". Please choose a different name."); setSaving(false); return; }
      if (existingEntry) {
        await updateDoc(doc(db, "entries", existingEntry.id), { entryName: entryName.trim(), picks, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "entries"), { contestId: contest.id, userId: user.uid, userName: user.displayName, entryName: entryName.trim(), picks, totalScore: 0, createdAt: serverTimestamp() });
      }
      setSavedEntry({ entryName: entryName.trim(), picks });
      setSubmitted(true);
      onSaved();
    } catch (e) { setError("Error saving entry. Please try again."); console.error(e); }
    setSaving(false);
  };

  if (submitted && savedEntry) {
    return (
      <div className="animate-fade-in" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontSize: 60, marginBottom: 12 }} className="animate-float">✅</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#00C853", letterSpacing: 2, marginBottom: 8 }}>ENTRY SAVED!</div>
        <p style={{ color: "rgba(255,255,255,0.5)", marginBottom: 24 }}>
          <strong style={{ color: "white" }}>{savedEntry.entryName}</strong> has been {existingEntry ? "updated" : "submitted"} successfully.
        </p>
        <div className="card" style={{ maxWidth: 480, margin: "0 auto 24px auto", textAlign: "left" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,215,0,0.6)", letterSpacing: 2, marginBottom: 12 }}>YOUR PICKS</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 6 }}>
            {TIERS.map((tier) => {
              const team = getTeam(savedEntry.picks["tier" + tier]);
              return (
                <div key={tier} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "6px 10px", display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ fontSize: 10, color: "rgba(255,215,0,0.6)", fontWeight: 700, minWidth: 18 }}>T{tier}</span>
                  {team ? <><Flag cc={team.cc} size={16} /><span style={{ fontSize: 12, fontWeight: 600 }}>{team.name}</span><span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginLeft: 2 }}>Grp {team.group}</span></> : <span style={{ color: "rgba(255,255,255,0.2)" }}>—</span>}
                </div>
              );
            })}
          </div>
        </div>
        <button onClick={() => { setSubmitted(false); setEntryName(""); setPicks({}); setSavedEntry(null); }} className="btn btn-outline">
          + Submit Another Entry
        </button>
      </div>
    );
  }

  const groupCounts = pickedGroups();
  const duplicateGroups = Object.entries(groupCounts).filter(([, count]) => count > 1).map(([g]) => g);

  return (
    <div className="animate-fade-in">
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: "#FFD700", letterSpacing: 2, marginBottom: 16 }}>
        {existingEntry ? "EDIT ENTRY" : "NEW ENTRY"}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: 1, marginBottom: 8 }}>ENTRY NAME</label>
        <input value={entryName} onChange={(e) => setEntryName(e.target.value)} placeholder="e.g. Evan - Entry 1" className="input" />
      </div>

      {/* Group overlap warning */}
      {duplicateGroups.length > 0 && (
        <div style={{ background: "#1a2d4a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "rgba(255,255,255,0.55)", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0 }}>ℹ️</span>
          <span>Just a heads up — you have multiple teams from {duplicateGroups.map((g, i) => <strong key={g} style={{ color: "rgba(255,255,255,0.8)" }}>Group {g}{i < duplicateGroups.length - 1 ? ", " : ""}</strong>)}. Up to 3 teams from the same group can advance to the knockouts so it's not an issue, but be aware those teams will face each other in the group stage.</span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>{picksComplete}/10 tiers picked</div>
        {allPicked && <span className="badge badge-green">All tiers complete</span>}
      </div>

      {TIERS.map((tier) => {
        const tierTeams = teamsForTier(tier);
        return (
          <div key={tier} className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#FFD700", letterSpacing: 1 }}>TIER {tier}</div>
              {picks["tier" + tier] && (() => {
                const t = getTeam(picks["tier" + tier]);
                return t ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Flag cc={t.cc} size={16} />
                    <span className="badge badge-green">{t.name}</span>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>Group {t.group}</span>
                  </div>
                ) : null;
              })()}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {tierTeams.map((team) => {
                const isSelected = picks["tier" + tier] === team.id;
                const groupAlreadyPicked = !isSelected && groupCounts[team.group] > 0;
                return (
                  <button key={team.id} onClick={() => handlePick(tier, team.id)}
                    style={{
                      padding: "7px 12px", borderRadius: 8, cursor: "pointer",
                      fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 13,
                      transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
                      border: isSelected ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.1)",
                      background: isSelected ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.04)",
                      color: isSelected ? "#FFD700" : "rgba(255,255,255,0.7)"
                    }}>
                    <Flag cc={team.cc} size={16} />
                    <span>{team.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                      background: isSelected ? "rgba(255,215,0,0.2)" : groupAlreadyPicked ? "rgba(255,152,0,0.2)" : "rgba(255,255,255,0.08)",
                      color: isSelected ? "#FFD700" : groupAlreadyPicked ? "#ff9800" : "rgba(255,255,255,0.4)"
                    }}>
                      Grp {team.group}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && <p className="error-text" style={{ marginBottom: 12 }}>{error}</p>}

      <button onClick={save} disabled={saving} className={"btn btn-lg " + (allPicked ? "btn-primary" : "btn-ghost")} style={{ borderRadius: 10, marginTop: 8, opacity: allPicked ? 1 : 0.5 }}>
        {saving ? "Saving..." : existingEntry ? "Update Entry" : "Submit Entry"}
      </button>
    </div>
  );
}

export default PickEntry;
